import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useMemo, useState } from 'react';

import type { SelectOptionConfig } from '@/lib/option-generators';
import { cn } from '@/lib/utils';

/**
 * VirtualSelect - A performant select component with optional virtualization
 *
 * STATE SEMANTICS:
 * - undefined: Initial/pristine state (never touched). Shows `placeholder`.
 * - null: User explicitly cleared selection. Shows `nullableLabel`.
 * - SelectOptionConfig<T>: User selected an option. Shows the option value.
 *
 * Note: undefined is a one-way door - once user interacts, it becomes null or an option.
 * The onChange callback never emits undefined, only null or a selected option.
 *
 * TODO: Consider adding native optgroup support if:
 * - We need it in 3+ places
 * - Accessibility audit requires semantic groups
 * - We disable virtualization for grouped selects
 *
 * Current approach: Use disabled items with isGroupHeader flag as pseudo-groups.
 * This works well for mobile-first touch interaction and virtualized scrolling.
 */
interface VirtualSelectProps<T extends string | number> {
	// The list of options to select from
	options: SelectOptionConfig<T>[];

	// Current selected value (used for comparison)
	value: SelectOptionConfig<T> | null | undefined;

	/**
	 * Optional custom display value or rendering function
	 * - displayValue: Simple string to show when an option is selected
	 * - renderDisplay: Full custom rendering function for the button content
	 */
	displayValue?: string | null;
	renderDisplay?: (props: {
		selectedOption: SelectOptionConfig<T> | null | undefined;
	}) => React.ReactNode;

	// Callback to report changes
	onChange: (newValue: SelectOptionConfig<T> | null) => void;

	// UI customization
	disabled?: boolean;
	buttonClassName?: string;
	menuClassName?: string;
	optionClassName?: string; // Custom className for options (useful for grouped selects)
	placeholder?: string; // Shown when value === undefined (initial state)

	// Nullable behavior
	nullable?: boolean; // Allow user to clear selection from dropdown
	nullableLabel?: string; // Label shown when value === null (default: '-')

	// Performance optimization
	enableVirtualization?: boolean;
	itemHeight?: number;
	maxVisibleItems?: number;
}

function VirtualSelect<T extends string | number>({
	options,
	value,
	displayValue,
	renderDisplay,
	onChange,
	disabled,
	buttonClassName,
	menuClassName,
	optionClassName,
	placeholder = 'Select...',
	nullable = false,
	nullableLabel = '-',
	enableVirtualization = false,
	itemHeight = 36,
	maxVisibleItems = 8,
}: VirtualSelectProps<T>) {
	const [parentElement, setParentElement] = useState<HTMLDivElement | null>(null);

	// Prepend nullable option if enabled
	const displayOptions = useMemo(() => {
		if (!nullable) {
			return options;
		}

		// Create special null option marker
		const nullOption: SelectOptionConfig<T> = {
			// @todo: this is ok i think
			value: null as unknown as T, // Sentinel value
			label: nullableLabel,
		};

		return [nullOption, ...options];
	}, [nullable, nullableLabel, options]);

	const virtualizer = useVirtualizer({
		count: displayOptions.length,
		getScrollElement: () => parentElement,
		estimateSize: () => itemHeight,
		overscan: 3, // Render 3 extra items above/below visible area
	});
	const containerHeight = maxVisibleItems * itemHeight;

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only trigger when dropdown opens/closes, not on every value change
	useEffect(() => {
		if (!parentElement) {
			return;
		}
		const selectedIndex = displayOptions.findIndex((option) => option.value === value?.value);
		if (selectedIndex !== -1) {
			virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
		}
	}, [parentElement]);

	return (
		<Listbox value={value ?? null} onChange={onChange} disabled={disabled} by="value">
			<ListboxButton
				className={cn(
					// 'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent',
					buttonClassName,
				)}
			>
				{renderDisplay
					? renderDisplay({ selectedOption: value })
					: // Default rendering if no custom function provided
						(displayValue ?? value?.label ?? placeholder)}
			</ListboxButton>

			<ListboxOptions
				anchor="bottom"
				onKeyDown={handleKeyDown}
				className={cn(
					'z-50 hidden min-w-[max(100px,var(--button-width))] rounded-lg border border-gray-200 bg-white px-1 py-1.5 shadow-lg [--anchor-gap:0.375rem] [--anchor-padding:0.8rem] focus:outline-none data-open:block',
					menuClassName,
				)}
				style={{
					// 12 px = padding-top + padding-bottom
					height: enableVirtualization ? `${containerHeight + 12}px` : 'auto',
					maxHeight: `min(${containerHeight + 12}px, 80vh)`,
					overflowY: enableVirtualization ? 'hidden' : 'auto',
				}}
			>
				{enableVirtualization ? (
					<div
						ref={(node) => setParentElement(node)}
						style={{
							height: '100%',
							overflowY: 'auto',
						}}
					>
						<div
							style={{
								height: `${virtualizer.getTotalSize()}px`,
								width: '100%',
								position: 'relative',
							}}
						>
							{virtualizer.getVirtualItems().map((virtualItem) => {
								const option = displayOptions[virtualItem.index];
								if (!option) {
									return null;
								}
								return (
									<ListboxOption
										className={cn(
											'group/listbox-option absolute flex w-full select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
											option.isGroupHeader
												? 'pointer-events-none cursor-default bg-blue-50 font-semibold text-blue-900 text-xs uppercase tracking-wide'
												: cn(
														'cursor-pointer hover:bg-gray-100 data-selected:bg-gray-200 data-selected:font-medium',
														optionClassName,
													),
										)}
										key={option.value}
										disabled={option.disabled ?? false}
										data-index={virtualItem.index}
										value={option}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: `${virtualItem.size}px`,
											transform: `translateY(${virtualItem.start}px)`,
										}}
									>
										<span className="truncate text-xs sm:text-sm">{option.label}</span>
										{!option.isGroupHeader && (
											<CheckIcon className="invisible size-3.5 shrink-0 text-gray-800 group-data-selected/listbox-option:visible" />
										)}
									</ListboxOption>
								);
							})}
						</div>
					</div>
				) : (
					displayOptions.map((option) => (
						<ListboxOption
							className={cn(
								'group flex select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
								option.isGroupHeader
									? 'pointer-events-none cursor-default bg-blue-50 font-semibold text-blue-900 text-xs uppercase tracking-wide'
									: cn(
											'cursor-pointer hover:bg-gray-100 data-selected:bg-gray-200 data-selected:font-medium',
											optionClassName,
										),
							)}
							key={option.value}
							value={option}
							disabled={option.disabled ?? false}
						>
							<span className="truncate text-xs sm:text-sm">{option.label}</span>
							{!option.isGroupHeader && (
								<CheckIcon className="invisible size-3.5 shrink-0 text-gray-800 group-data-selected:visible" />
							)}
						</ListboxOption>
					))
				)}
			</ListboxOptions>
		</Listbox>
	);
}

function handleKeyDown(event: React.KeyboardEvent) {
	if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
		// ====================================================================
		// INTENTIONAL DISABLING OF KEYBOARD NAVIGATION (ARROW KEYS)
		// ====================================================================
		//
		// **What is happening here?**
		// We are explicitly preventing the default behavior of the Up and Down arrow
		// keys for this component, effectively disabling keyboard navigation.
		//
		// **Why?**
		// There is a fundamental architectural conflict between Headless UI's state
		// machine and TanStack Virtual's dynamic rendering:
		//
		// 1.  **Headless UI's Assumption:** It is designed as a state machine that
		//     assumes it has a complete and stable list of all its child
		//     <Listbox.Option> components. It uses this internal list to calculate
		//     the "next" or "previous" item to focus.
		//
		// 2.  **TanStack Virtual's Reality:** Its entire purpose is to constantly
		//     mount and unmount <Listbox.Option> components as the user scrolls to
		//     maintain performance. This means Headless UI's internal list of options
		//     is constantly changing, which the state machine was not designed for.
		//
		// **The Bugs This Caused:**
		// This conflict created severe, hard-to-fix bugs, including:
		//   - A "runaway scroll" feedback loop where pressing the Down arrow once
		//     caused the list to scroll uncontrollably to the end.
		//   - Crashes when navigating past the boundaries of the virtualized list.
		//
		// **The Decision:**
		// Given that this is a mobile-first fitness logging application where touch is
		// the primary interaction model, keyboard navigation is a non-critical edge case.
		// The significant effort required to build a fully custom, virtualization-aware
		// navigation handler is deferred in favor of guaranteeing a stable and crash-free
		// experience for 99.9% of users.
		//
		// **Path to a Future Fix:**
		// Understand how headless ui manages the focus and state and what happens
		// in the keyDown event. and try to intercept the keyDown in certain cases.
		// or when the Listbox component updates to use the virtualizer.
		// ====================================================================
		event.preventDefault();
		event.stopPropagation();
	}
}

export { VirtualSelect };
export type { VirtualSelectProps };
